import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { REVIEW_PHOTO_COUNT, REVIEW_PHOTO_EXT, REVIEW_PHOTO_PUBLIC_DIR } from "./reviewAssets";

function mulberry32(seed) {
  return function random() {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

function buildTiles(publicUrl) {
  const rand = mulberry32(hashSeed("hersaltystudio-reviews-wall-v3"));
  const base = publicUrl.endsWith("/") ? publicUrl.slice(0, -1) : publicUrl;
  const cols = 4;
  const rows = 5;
  const cellW = 100 / cols;
  const tiles = [];
  for (let i = 0; i < REVIEW_PHOTO_COUNT; i += 1) {
    const n = String(i + 1).padStart(2, "0");
    const col = i % cols;
    const row = Math.floor(i / cols);
    const widthPct = 19 + rand() * 9;
    const jitterX = (rand() - 0.5) * (cellW * 0.55);
    const left = clamp(col * cellW + (cellW - widthPct) / 2 + jitterX, -1, 101 - widthPct);
    const rowSpan = 100 / rows;
    const jitterY = (rand() - 0.5) * (rowSpan * 0.5);
    const top = clamp(row * rowSpan + rowSpan * 0.04 + jitterY, -1, 92);
    tiles.push({
      id: i,
      src: `${base}/${REVIEW_PHOTO_PUBLIC_DIR}/${n}.${REVIEW_PHOTO_EXT}`,
      left,
      top,
      widthPct,
      rotZ: (rand() - 0.5) * 20,
      rotX: 2 + rand() * 6,
      rotY: (rand() - 0.5) * 11,
      tz: rand() * 32 - 16,
      zi: i + 1,
    });
  }
  return tiles;
}

function tileTransform(tile, lifted) {
  const { rotX, rotY, rotZ, tz } = tile;
  if (!lifted) {
    return `translate3d(0, 0, ${tz}px) rotateX(${rotX}deg) rotateY(${rotY}deg) rotateZ(${rotZ}deg) scale(1)`;
  }
  return `translate3d(0, 0, ${tz + 52}px) rotateX(${rotX * 0.72}deg) rotateY(${rotY * 0.72}deg) rotateZ(${rotZ * 0.35}deg) scale(1.06)`;
}

export default function ReviewPhotoWall() {
  const publicUrl = process.env.PUBLIC_URL || "";
  const tiles = useMemo(() => buildTiles(publicUrl), [publicUrl]);
  const [hovered, setHovered] = useState(null);
  const [stuck, setStuck] = useState(null);
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const finePointer = useRef(true);

  useEffect(() => {
    setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    finePointer.current = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }, []);

  const focused = hovered !== null ? hovered : stuck;

  const onPointerEnter = useCallback((index) => {
    if (finePointer.current) setHovered(index);
  }, []);

  const onPointerLeave = useCallback(() => {
    setHovered(null);
  }, []);

  const onTileClick = useCallback(
    (index) => {
      if (finePointer.current) return;
      setStuck((prev) => (prev === index ? null : index));
    },
    []
  );

  const onStageClick = useCallback(() => {
    if (!finePointer.current) setStuck(null);
  }, []);

  const parallaxRef = useRef({ x: 0, y: 0 });
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || reducedMotion) return undefined;

    const onMove = (e) => {
      const r = stage.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      const ny = ((e.clientY - r.top) / r.height - 0.5) * 2;
      parallaxRef.current = { x: nx, y: ny };
      setParallax({ x: nx, y: ny });
    };
    const onLeave = () => {
      parallaxRef.current = { x: 0, y: 0 };
      setParallax({ x: 0, y: 0 });
    };
    stage.addEventListener("pointermove", onMove);
    stage.addEventListener("pointerleave", onLeave);
    return () => {
      stage.removeEventListener("pointermove", onMove);
      stage.removeEventListener("pointerleave", onLeave);
    };
  }, [reducedMotion]);

  const prx = reducedMotion ? 0 : parallax.x * 5;
  const pry = reducedMotion ? 0 : parallax.y * -4;

  return (
    <div className="reviews-photo-wall" role="region" aria-label="Customer review photos">
      <div
        ref={stageRef}
        className="reviews-photo-wall__stage"
        onClick={onStageClick}
        role="presentation"
        style={{
          transform: `rotateX(${2.5 + pry}deg) rotateY(${-3 + prx}deg)`,
        }}
      >
        {tiles.map((tile) => {
          const isUp = focused === tile.id;
          return (
            <button
              key={tile.id}
              type="button"
              className={`reviews-photo-wall__tile${isUp ? " reviews-photo-wall__tile--up" : ""}`}
              style={{
                left: `${tile.left}%`,
                top: `${tile.top}%`,
                width: `${tile.widthPct}%`,
                zIndex: isUp ? 400 : tile.zi,
                transform: tileTransform(tile, isUp),
              }}
              aria-label={`Customer review photo ${tile.id + 1}`}
              onPointerEnter={() => onPointerEnter(tile.id)}
              onPointerLeave={onPointerLeave}
              onClick={(e) => {
                e.stopPropagation();
                onTileClick(tile.id);
              }}
              onFocus={() => onPointerEnter(tile.id)}
              onBlur={onPointerLeave}
            >
              <span className="reviews-photo-wall__frame">
                <img src={tile.src} alt="" loading="lazy" decoding="async" width={400} height={500} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
